import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { Trans, useLingui } from '@lingui/react/macro';

import config from '@/config';
import Button from '@/design_system/Button';
import InputText from '@/design_system/InputText';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import { useCheckSsoMutation } from '@/models/user';

export const EmailStep = ({
  onNext,
  mfaExpired,
}: {
  onNext: (email: string) => void;
  mfaExpired: boolean;
}) => {
  const { t } = useLingui();

  const mfaExpiredError = t({
    id: 'login.error.mfa-expired',
    message: 'The 2-step verification token has expired. Please try logging in again.',
  });

  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [error, setError] = useState(
    mfaExpired ? mfaExpiredError : (searchParams.get('sso_error') ?? '')
  );

  const {
    mutateAsync: checkSso,
    isPending: isCheckSsoPending,
    isSuccess: isCheckSsoSuccess,
  } = useCheckSsoMutation();

  async function handleInitiateSso() {
    try {
      const { sso } = await checkSso(email);

      if (sso) {
        const params = new URLSearchParams({ email });
        const redirect = searchParams.get('redirect') ?? '';

        if (redirect) {
          params.set('redirect', redirect);
        }

        window.location.href = `${config.apiUrl}/sso/authorize?${params.toString()}`;
      } else {
        onNext(email);
      }
    } catch (err: any) {
      setError(
        (err as Error)?.message ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
      );
    }
  }

  return (
    <Stack gap="0.5rem">
      <Title size="200" as="h1" align="center">
        <Trans id="login.title">Log in to continue</Trans>
      </Title>
      <form
        onSubmit={(evt) => {
          evt.preventDefault();
          handleInitiateSso();
        }}
      >
        <Stack gap="1.5rem">
          <InputText
            isRequired
            name="email"
            label={<Trans id="login.input.email.label">Email</Trans>}
            type="email"
            inputMode="email"
            placeholder={t({
              id: 'login.input.email.placeholder',
              message: 'Enter your email address...',
            })}
            value={email}
            onChange={setEmail}
          />
          {/** This is a hidden input to allow browsers to understand that this is a login form, and to autocomplete the password */}
          <input type="password" name="password" hidden />
          {error && (
            <Paragraph size="100" weight="medium" color="danger" align="center">
              {error}
            </Paragraph>
          )}
          <Button type="submit" isLoading={isCheckSsoPending || isCheckSsoSuccess}>
            <Trans id="login.continue">Continue</Trans>
          </Button>
        </Stack>
      </form>
    </Stack>
  );
};
